import { Outlet } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Search } from "lucide-react";
import CommandPalette from "@/components/CommandPalette";
import MiraWidget from "@/components/feedback/MiraWidget";
import Sidebar from "@/components/Sidebar";
import PageHeader, { resolvePage } from "@/components/PageHeader";
import { useLocation } from "react-router-dom";
import FieldSessionBanner from "@/components/FieldSessionBanner";
import BridgeStatusBanner from "@/components/integrations/BridgeStatusBanner";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n";

export const Layout = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  // iter274 — the palette is now people-first for field roles (techs +
  // vendors only, enforced server-side), so every signed-in user gets it.
  const paletteAllowed = !!user;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const mobilePage = resolvePage(useLocation().pathname);

  // Global Cmd+K / Ctrl+K trigger. Attached at the layout level so
  // every protected route inherits it.
  useEffect(() => {
    if (!paletteAllowed) return;
    const onKeyDown = (e) => {
      const key = (e.key || "").toLowerCase();
      if (key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        // Desktop: focus the header search field; phones: toggle the modal.
        if (window.matchMedia("(min-width: 768px)").matches) window.dispatchEvent(new Event("global-search:focus"));
        else setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [paletteAllowed]);

  return (
    <div className="min-h-screen flex">
      <Sidebar
        mobileOpen={mobileNavOpen}
        onNavigate={() => setMobileNavOpen(false)}
        onOpenPalette={() => { setMobileNavOpen(false); setPaletteOpen(true); }}
      />

      {/* Mobile scrim — tap outside to close the drawer */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile-only bar so phones keep access to navigation.
            Sticky + safe-area padding: with viewport-fit=cover and the
            black-translucent iOS status bar, the page runs under the
            notch in PORTRAIT (top inset ≈ 47-59px), which used to cover
            the whole 48px bar → hamburger unreachable. Landscape has a
            0 top inset, which is why it only worked sideways. */}
        <div className="md:hidden sticky top-0 z-[45] flex items-center gap-2 h-12 px-3 border-b abs-mobile-bar abs-safe-top" data-testid="top-header">
          <button
            className="p-2 -ml-1 rounded-md min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            aria-label={t("Toggle Navigation")}
            aria-expanded={mobileNavOpen}
            data-testid="mobile-nav-toggle"
          >
            {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <img src="/abs-logo.png" alt="ABS" className="abs-logo h-5 w-auto" />
          {mobilePage && (
            <span className="ml-1 truncate text-sm font-semibold" style={{ fontFamily: "var(--font-heading)" }} data-testid="mobile-page-title">
              {t(mobilePage.title || mobilePage.label)}
            </span>
          )}
          {paletteAllowed && (
            <button type="button" onClick={() => setPaletteOpen(true)} aria-label={t("Quick search")} title={t("Quick search")}
              className="ml-auto p-2 -mr-1 rounded-md min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation" data-testid="mobile-open-command-palette">
              <Search className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Desktop page header — icon + page name, driven by the sidebar's nav config. */}
        <PageHeader onOpenPalette={paletteAllowed ? () => window.dispatchEvent(new Event("global-search:focus")) : undefined} />

        {/* w-full is required: `.top-layout-content` has `margin: 0 auto`,
            and auto cross-axis margins stop a flex item from stretching. */}
        <main className="top-layout-content flex-1 w-full min-w-0">
          <FieldSessionBanner />
          <BridgeStatusBanner />
          <Outlet />
        </main>
      </div>

      {paletteAllowed && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}
      <MiraWidget />
    </div>
  );
};

export default Layout;
